import State from 'country-state-city/lib/state';
import React, { useMemo, useState } from 'react';
import type { TextStyle, ViewStyle } from 'react-native';

import { FormInput, type FormInputProps } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { TypographyToken } from '@/theme/typography';

import { FieldTrigger } from './field-trigger';
import { SearchableSelectSheet, type SearchableSelectItem } from './searchable-select-sheet';

const strings = STRINGS.common.stateProvince;
const pickerStrings = STRINGS.common.countryRegionPicker;

/** State/Province is free text (max 100) when the country has no supported list. */
const MAX_STATE_LENGTH = 100;

export type StateProvinceFieldProps = {
  label?: string;
  required?: boolean;
  /** Selected country ISO code; drives whether this is a combobox or a free-text field. */
  country?: string | null;
  value?: string | null;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  status?: 'default' | 'error';
  errorMessage?: string;
  // Styling passthrough (matches the surrounding form fields).
  uppercaseLabel?: boolean;
  labelVariant?: TypographyToken;
  labelGap?: number;
  labelStyle?: TextStyle | TextStyle[];
  triggerVariant?: TypographyToken;
  triggerStyle?: ViewStyle | ViewStyle[];
  inputVariant?: TypographyToken;
  inputStyle?: TextStyle | TextStyle[];
  contentStyle?: ViewStyle | ViewStyle[];
  disableStyle?: ViewStyle | ViewStyle[];
};

/**
 * Address State/Province field. Renders a searchable combobox when the selected country has a
 * supported state list (placeholder "Select State/Province"); a free-text field otherwise
 * (placeholder "Enter State/Province", max 100 chars). With no country selected it stays a
 * combobox showing "--" that opens to "No results".
 */
export function StateProvinceField({
  label,
  required = false,
  country,
  value,
  onValueChange,
  disabled = false,
  status = 'default',
  errorMessage,
  uppercaseLabel = false,
  labelVariant,
  labelGap,
  labelStyle,
  triggerVariant,
  triggerStyle,
  inputVariant,
  inputStyle,
  contentStyle,
  disableStyle,
}: StateProvinceFieldProps) {
  const [open, setOpen] = useState(false);

  const stateItems = useMemo<SearchableSelectItem[]>(
    () =>
      country
        ? State.getStatesOfCountry(country)
            .map(state => ({ value: state.name, label: state.name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [country],
  );

  const hasStateList = country ? stateItems.length > 0 : false;

  // Free-text field: country selected but no supported state list.
  if (country && !hasStateList) {
    const sharedProps = {
      uppercaseLabel,
      labelVariant,
      inputVariant,
      labelGap,
      labelStyle,
      inputStyle,
      contentStyle,
    } satisfies Partial<FormInputProps>;
    return (
      <FormInput
        {...sharedProps}
        label={label ?? ''}
        required={required}
        disabled={disabled}
        disableStyle={disableStyle}
        value={value ?? ''}
        onChangeText={onValueChange}
        placeholder={strings.enterPlaceholder}
        status={status}
        errorMessage={errorMessage}
        autoCapitalize="words"
        maxLength={MAX_STATE_LENGTH}
      />
    );
  }

  // Combobox: either a real state list, or the empty "--" / "No results" state (no country).
  const placeholder = country ? strings.selectPlaceholder : strings.noCountryPlaceholder;

  return (
    <>
      <FieldTrigger
        label={label}
        required={required}
        uppercaseLabel={uppercaseLabel}
        labelVariant={labelVariant}
        labelGap={labelGap}
        labelStyle={labelStyle}
        placeholder={placeholder}
        displayValue={value || null}
        disabled={disabled}
        status={status}
        errorMessage={errorMessage}
        triggerVariant={triggerVariant}
        triggerStyle={triggerStyle}
        disableStyle={disableStyle}
        onPress={() => setOpen(true)}
      />
      <SearchableSelectSheet
        open={open}
        onOpenChange={setOpen}
        title={label ?? strings.selectPlaceholder}
        searchPlaceholder={strings.enterPlaceholder}
        items={stateItems}
        selectedValue={value}
        onSelect={onValueChange}
        grouped
        emptyText={pickerStrings.noResults}
      />
    </>
  );
}

export default StateProvinceField;
